import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  BottomSheet,
  Button,
  Card,
  Dialog,
  IconButton,
  Menu,
  Notice,
  RadioCard,
  Skeleton,
  StateScreen,
  TextField,
  iconMore,
  useSnackbar,
  Segmented,
} from '@sauda/ui/kit';
import { Screen, useChrome } from '../../app/chrome.js';
import { LinkButton } from '../../app/LinkButton.js';
import { useAuth } from '../../auth/AuthContext.js';
import { ApiError, api } from '../../lib/api.js';
import type { Branch } from '../../lib/types.js';
import { BranchForm, type BranchValues } from './BranchForm.js';

/** One branch: its details, how new visits are routed, the times offered to clients, and archiving. */
export function BranchScreen() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const auth = useAuth();
  const chrome = useChrome();
  const cache = useQueryClient();
  const navigate = useNavigate();
  const snack = useSnackbar();
  const canManage = auth.allowed('branches.manage') && !chrome.readOnly;
  const [editing, setEditing] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMembers, setHasMembers] = useState(false);
  const branches = useQuery({
    queryKey: ['branches', auth.agencyId],
    queryFn: () => api<Branch[]>('/branches'),
  });
  const branch = branches.data?.find((b) => b.id === id);
  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<Branch>(`/branches/${id}`, { method: 'PATCH', body }),
    onSuccess: async () => {
      setEditing(false);
      setError(null);
      await cache.invalidateQueries({ queryKey: ['branches'] });
      snack({ text: t('branches.saved') });
    },
    onError: () => setError(t('branches.generic')),
  });
  const archive = useMutation({
    mutationFn: () => api<Branch>(`/branches/${id}/archive`, { method: 'POST' }),
    onSuccess: async () => {
      await cache.invalidateQueries({ queryKey: ['branches'] });
      void navigate('/branches');
    },
    onError: (e) => {
      setArchiving(false);
      const has = e instanceof ApiError && e.code === 'BRANCH_HAS_MEMBERS';
      setHasMembers(has);
      setError(has ? t('branches.hasMembers') : t('branches.generic'));
    },
  });

  if (branches.isSuccess && branch === undefined)
    return (
      <Screen title={t('branches.title')} back="/branches">
        <StateScreen kind="empty" compact title={t('branches.notFound')} />
      </Screen>
    );
  if (branch === undefined)
    return (
      <Screen title={t('branches.title')} back="/branches">
        <Skeleton height={120} />
      </Screen>
    );
  return (
    <Screen title={branch.name} back="/branches" eyebrow={branch.area ?? undefined}>
      {error === null ? null : <Notice tone="error">{error}</Notice>}
      {hasMembers ? (
        <LinkButton variant="tonal" to="/team">
          {t('branches.openTeam')}
        </LinkButton>
      ) : null}
      <Card>
        <h2 className="k-t-title-sm">{t('branches.details')}</h2>
        <p className="k-t-body">{branch.address ?? t('branches.noAddress')}</p>
        <p className="k-t-body k-muted">{branch.phone ?? t('branches.noPhone')}</p>
        {canManage && branch.archivedAt === null ? (
          <div className="chips-row">
            <Button variant="tonal" onClick={() => setEditing(true)}>
              {t('branches.edit')}
            </Button>
            <Menu
              label={t('branches.more')}
              items={[
                {
                  key: 'archive',
                  label: t('branches.archive'),
                  destructive: true,
                  onSelect: () => setArchiving(true),
                },
              ]}
              trigger={(props) => (
                <IconButton {...props} label={t('branches.more')} icon={iconMore} />
              )}
            />
          </div>
        ) : null}
      </Card>
      <VisitSettings
        key={`${branch.visitRouting}-${branch.visitSlots.join('|')}`}
        branch={branch}
        canManage={canManage && branch.archivedAt === null}
        busy={save.isPending}
        onSave={(body) => save.mutate(body)}
      />
      <BottomSheet
        open={editing}
        onClose={() => setEditing(false)}
        label={t('branches.edit')}
        title={t('branches.edit')}
        closeLabel={t('app.close')}
      >
        <BranchForm
          initial={branch}
          busy={save.isPending}
          submitLabel={t('branches.saveChanges')}
          onSubmit={(values: BranchValues) =>
            save.mutate({
              name: values.name,
              area: values.area ?? '',
              address: values.address ?? '',
              phone: values.phone ?? '',
            })
          }
        />
      </BottomSheet>
      <Dialog
        open={archiving}
        onClose={() => setArchiving(false)}
        title={t('branches.archiveTitle', { name: branch.name })}
        destructive
        actions={
          <>
            <Button variant="text" onClick={() => setArchiving(false)}>
              {t('app.cancel')}
            </Button>
            <Button destructive loading={archive.isPending} onClick={() => archive.mutate()}>
              {t('branches.archive')}
            </Button>
          </>
        }
      >
        {t('branches.archiveBody')}
      </Dialog>
    </Screen>
  );
}

/** Routing of new visit requests and up to three suggested times for the public page. */
function VisitSettings({
  branch,
  canManage,
  busy,
  onSave,
}: {
  branch: Branch;
  canManage: boolean;
  busy: boolean;
  onSave: (body: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation();
  const [routing, setRouting] = useState(branch.visitRouting);
  const [slots, setSlots] = useState<string[]>([...branch.visitSlots, '', '', ''].slice(0, 3));
  const [response, setResponse] = useState(branch.visitResponse);
  const dirty =
    routing !== branch.visitRouting ||
    response !== branch.visitResponse ||
    slots.filter((s) => s.trim() !== '').join('|') !== branch.visitSlots.join('|');
  return (
    <Card>
      <h2 className="k-t-title-sm">{t('branches.visitSettings')}</h2>
      <fieldset className="form-stack" disabled={!canManage}>
        <legend className="k-t-label">{t('branches.routing')}</legend>
        <RadioCard
          name="routing"
          label={t('branches.routeSharer')}
          description={t('branches.routeSharerHelp')}
          checked={routing === 'sharer'}
          onChange={() => setRouting('sharer')}
        />
        <RadioCard
          name="routing"
          label={t('branches.routeManager')}
          description={t('branches.routeManagerHelp')}
          checked={routing === 'manager'}
          onChange={() => setRouting('manager')}
        />
      </fieldset>
      <span className="k-t-label-lg">{t('branches.response')}</span>
      <Segmented
        label={t('branches.response')}
        value={response}
        onChange={(v) => setResponse(v as typeof response)}
        options={[
          { value: 'within_an_hour', label: t('branches.response_within_an_hour') },
          { value: 'today', label: t('branches.response_today') },
          { value: 'within_a_day', label: t('branches.response_within_a_day') },
        ]}
      />
      <div className="form-stack">
        <p className="k-t-label">{t('branches.slots')}</p>
        {slots.map((slot, index) => (
          <TextField
            key={index}
            label={t('branches.slotN', { n: index + 1 })}
            placeholder={t('branches.slotExample')}
            value={slot}
            disabled={!canManage}
            onChange={(e) => setSlots(slots.map((s, i) => (i === index ? e.target.value : s)))}
          />
        ))}
      </div>
      {canManage ? (
        <Button
          size="lg"
          loading={busy}
          disabled={!dirty}
          onClick={() =>
            onSave({
              visitRouting: routing,
              visitResponse: response,
              visitSlots: slots.map((s) => s.trim()).filter((s) => s !== ''),
            })
          }
        >
          {t('branches.saveVisitSettings')}
        </Button>
      ) : null}
    </Card>
  );
}
